"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.isAltiumSheetEntryNamingEligible = isAltiumSheetEntryNamingEligible;
exports.isAltiumPairedChildPortNamingEligible = isAltiumPairedChildPortNamingEligible;
const altiumProjectFileTypes_1 = require("../../project/altiumProjectFileTypes");
const eligibilityIndexes = new WeakMap();
function isAltiumSheetEntryNamingEligible(group, sheetEntryLabel, hierarchyMode, includeMatchingPeer = true) {
    const index = getEligibilityIndex(group, hierarchyMode);
    const regime = includeMatchingPeer ? index.withPeer : index.labelOnly;
    return regime.sheetEntryEligibility.get(sheetEntryLabel) ?? false;
}
function isAltiumPairedChildPortNamingEligible(group, portLabel, hierarchyMode, includeMatchingPeer = true, allowRelayBridgeRescue = false) {
    const portMetadata = portLabel.strategyMetadata?.['port-metadata'];
    if (!portMetadata) {
        return true;
    }
    const index = getEligibilityIndex(group, hierarchyMode);
    const instance = index.labelInstances.get(portLabel);
    if (instance?.pathUniqueId !== portMetadata.pathUniqueId || !instance.sheetSymbol) {
        return true;
    }
    const regime = includeMatchingPeer ? index.withPeer : index.labelOnly;
    const pair = getPairKey(instance.sheetSymbol.uniqueId, portMetadata.portName);
    const repeatedPair = getRepeatedPairKey(pair, portMetadata.channelIndex);
    const isLabelOnlyIneligible = index.labelOnly.ineligibleUnscopedPairs.has(pair);
    const isPeerEligible = !index.withPeer.ineligibleUnscopedPairs.has(pair);
    const localNet = index.labelNets.get(portLabel);
    if (isLabelOnlyIneligible &&
        isPeerEligible &&
        localNet?.netObjects.some((object) => object.connection) &&
        hasDifferentHierarchyIdentity(group, portMetadata.portName)) {
        return true;
    }
    if (allowRelayBridgeRescue && isAltiumStrictRelayBridgeChildPort(portLabel, hierarchyMode, index)) {
        return true;
    }
    return !regime.ineligibleUnscopedPairs.has(pair) && !regime.ineligibleRepeatedPairs.has(repeatedPair);
}
function isAltiumStrictRelayBridgeChildPort(portLabel, hierarchyMode, index) {
    if (hierarchyMode !== altiumProjectFileTypes_1.HierarchyMode.StrictHierarchical) {
        return false;
    }
    const portMetadata = portLabel.strategyMetadata?.['port-metadata'];
    const instance = index.labelInstances.get(portLabel);
    const localNet = index.labelNets.get(portLabel);
    if (!portMetadata || !instance?.sheetSymbol || !localNet) {
        return false;
    }
    const parentEntries = Array.from(index.labelNets.keys()).filter((label) => isPairedPlainScalarEntry(label, instance.sheetSymbol.uniqueId, portMetadata.portName, instance.pathUniqueId));
    if (parentEntries.length !== 1) {
        return false;
    }
    const parentEntry = parentEntries[0];
    const parentNet = index.labelNets.get(parentEntry);
    const bridgeEntryLabels = parentNet?.netObjects.map((object) => object.label).filter(isSheetEntry) ?? [];
    const bridgeEntries = bridgeEntryLabels.filter(isPlainScalarSheetEntry);
    if (!parentNet ||
        !parentNet.netObjects.some((object) => object.wire) ||
        parentNet.netObjects.some((object) => object.connection) ||
        bridgeEntries.length !== bridgeEntryLabels.length ||
        bridgeEntries.length < 2) {
        return false;
    }
    const childPorts = bridgeEntries
        .map((entry) => findPairedChildPort(entry, index))
        .filter((candidate) => candidate !== undefined);
    if (childPorts.length !== bridgeEntries.length) {
        return false;
    }
    const childSides = childPorts.map((childPort) => index.labelNets.get(childPort));
    if (childSides.some((net) => !net || !(net.netObjects.some((object) => object.connection) || hasGenuineRelayContinuation(net, index)))) {
        return false;
    }
    return childPorts.some((childPort) => hasGenuineRelayContinuation(index.labelNets.get(childPort), index));
}
function isPlainScalarSheetEntry(label) {
    const metadata = label?.strategyMetadata?.['sheet-entry-metadata'];
    return (label?.labelType === 'sheet-entry' &&
        metadata?.isRepeated === false &&
        metadata.busRange === undefined &&
        label.strategyMetadata?.busRangeMember !== true &&
        label.strategyMetadata?.harness === undefined);
}
function isPairedPlainScalarEntry(label, sheetSymbolUniqueId, portName, childPathUniqueId) {
    const metadata = label.strategyMetadata?.['sheet-entry-metadata'];
    return (isPlainScalarSheetEntry(label) &&
        metadata?.sheetSymbolUniqueId === sheetSymbolUniqueId &&
        isDirectChildPath(childPathUniqueId, metadata.parentPathUniqueId, sheetSymbolUniqueId) &&
        metadata.sheetEntryName.toLowerCase() === portName.toLowerCase());
}
function isSheetEntry(label) {
    return label?.labelType === 'sheet-entry';
}
function isDirectChildPath(childPathUniqueId, parentPathUniqueId, sheetSymbolUniqueId) {
    return (childPathUniqueId === `${parentPathUniqueId}\\${sheetSymbolUniqueId}` ||
        childPathUniqueId === `${parentPathUniqueId}/${sheetSymbolUniqueId}`);
}
function findPairedChildPort(entry, index) {
    const metadata = entry.strategyMetadata?.['sheet-entry-metadata'];
    if (!metadata) {
        return undefined;
    }
    const ports = Array.from(index.labelNets.keys()).filter((label) => {
        const portMetadata = label.strategyMetadata?.['port-metadata'];
        const instance = index.labelInstances.get(label);
        return (label.labelType === 'port' &&
            portMetadata?.portName.toLowerCase() === metadata.sheetEntryName.toLowerCase() &&
            instance?.sheetSymbol?.uniqueId === metadata.sheetSymbolUniqueId &&
            portMetadata.pathUniqueId === instance.pathUniqueId);
    });
    return ports.length === 1 ? ports[0] : undefined;
}
function hasGenuineRelayContinuation(net, index) {
    return (!net.netObjects.some((object) => object.connection) &&
        !net.netObjects.some((object) => object.label && isNonSheetEntryNamingLabel(object.label) && object.label.labelType !== 'port') &&
        net.netObjects
            .map((object) => object.label)
            .filter(isPlainScalarSheetEntry)
            .some((entry) => findPairedChildPort(entry, index) !== undefined));
}
function getEligibilityIndex(group, hierarchyMode) {
    let indexesByMode = eligibilityIndexes.get(group);
    const existing = indexesByMode?.get(hierarchyMode);
    if (existing?.mutationVersion === group.getMutationVersion()) {
        return existing.index;
    }
    const index = {
        labelNets: new Map(),
        labelInstances: new Map(),
        withPeer: {
            sheetEntryEligibility: new Map(),
            ineligibleUnscopedPairs: new Set(),
            ineligibleRepeatedPairs: new Set(),
        },
        labelOnly: {
            sheetEntryEligibility: new Map(),
            ineligibleUnscopedPairs: new Set(),
            ineligibleRepeatedPairs: new Set(),
        },
    };
    for (const [instance, nets] of group.getNets()) {
        for (const net of nets) {
            for (const label of net.labels) {
                index.labelNets.set(label, net);
                index.labelInstances.set(label, instance);
            }
        }
    }
    for (const label of index.labelNets.keys()) {
        if (label.labelType !== 'sheet-entry') {
            continue;
        }
        const net = findNetContainingLabel(index, label);
        const eligibleByLabel = net?.netObjects.some((object) => isNonSheetEntryNamingLabel(object.label)) ?? false;
        const eligibleByPeer = eligibleByLabel || hasMatchingSheetEntryPeer(net, label, hierarchyMode);
        index.labelOnly.sheetEntryEligibility.set(label, eligibleByLabel);
        index.withPeer.sheetEntryEligibility.set(label, eligibleByPeer);
        const metadata = label.strategyMetadata?.['sheet-entry-metadata'];
        if (!metadata) {
            continue;
        }
        const pair = getPairKey(metadata.sheetSymbolUniqueId, metadata.sheetEntryName);
        for (const [regime, isEligible] of [
            [index.withPeer, eligibleByPeer],
            [index.labelOnly, eligibleByLabel],
        ]) {
            if (isEligible) {
                continue;
            }
            if (metadata.isRepeated) {
                regime.ineligibleRepeatedPairs.add(getRepeatedPairKey(pair, metadata.channelIndex));
            }
            else {
                regime.ineligibleUnscopedPairs.add(pair);
            }
        }
    }
    if (!indexesByMode) {
        indexesByMode = new Map();
        eligibilityIndexes.set(group, indexesByMode);
    }
    indexesByMode.set(hierarchyMode, { mutationVersion: group.getMutationVersion(), index });
    return index;
}
function findNetContainingLabel(index, label) {
    return index.labelNets.get(label);
}
function isNonSheetEntryNamingLabel(label) {
    if (label?.labelType !== 'net-label' &&
        label?.labelType !== 'power-port' &&
        label?.labelType !== 'port' &&
        label?.labelType !== 'off-sheet-connector') {
        return false;
    }
    return (label.labelType !== 'port' ||
        label.canNameNet ||
        label.strategyMetadata?.harness?.harnessSource === 'synthetic-port');
}
function hasDifferentHierarchyIdentity(group, identity) {
    const foldedIdentity = identity.toLowerCase();
    return group
        .getNetLabels()
        .some((label) => (label.labelType === 'port' || label.labelType === 'sheet-entry') &&
        label.originalLabel.toLowerCase() !== foldedIdentity);
}
function hasMatchingSheetEntryPeer(net, sheetEntryLabel, hierarchyMode) {
    const metadata = sheetEntryLabel.strategyMetadata?.['sheet-entry-metadata'];
    if (!net ||
        !metadata ||
        hierarchyMode !== altiumProjectFileTypes_1.HierarchyMode.StrictHierarchical ||
        metadata.isRepeated !== false ||
        sheetEntryLabel.strategyMetadata?.harness ||
        !net.netObjects.some((object) => object.wire) ||
        net.netObjects.some((object) => object.connection)) {
        return false;
    }
    const entryName = metadata.sheetEntryName;
    return net.netObjects.some((object) => {
        const candidate = object.label;
        if (candidate === sheetEntryLabel || candidate?.labelType !== 'sheet-entry') {
            return false;
        }
        const candidateMetadata = candidate.strategyMetadata?.['sheet-entry-metadata'];
        return (candidateMetadata?.sheetEntryName === entryName &&
            candidateMetadata.isRepeated === false &&
            !candidate.strategyMetadata?.harness);
    });
}
function getPairKey(sheetSymbolUniqueId, entryName) {
    return `${sheetSymbolUniqueId}\u0000${entryName.toLowerCase()}`;
}
function getRepeatedPairKey(pair, channelIndex) {
    return `${pair}\u0000${String(channelIndex)}`;
}
//# sourceMappingURL=AltiumSheetEntryNamingEligibility.js.map